import { MatDialogRef } from '@angular/material/dialog';
import { ConfirmationComponent } from '@components/confirmation/1.0';
import { DeepCloneService } from '@services/utilities/1.0/deep-clone.service';
import { throwError } from 'rxjs';
import { first } from 'rxjs/operators';

export const userMixin = (Base) =>
  class extends Base {
    emptyUsersMsg = false;
    reInviteSuccess = false;
    reInviteError = false;
    reInviteLoading = false;
    onboardBrandError = false;
    onboardBrandSuccess = false;
    statusError = false;
    showLoadError = false;
    statusChangeError = false;
    statusChangeSuccess = false;
    deleteUserSuccess = false;
    deleteUserError = false;
    isDeleteUser = false;
    isResetPassword = false;
    resetPasswordError = false;
    resetPasswordSuccess = false;
    reInvitedUser: string;
    isStatusChange = false;
    changedUserId = '';
    dialogRef: MatDialogRef<ConfirmationComponent>;

    reInviteUser(userId) {
      this.resetMessages();
      this.reInvitedUser = userId;
      let modalContent = {
        title: this.translateService.instant('users.grid.re-inviteTitle'),
        message: this.translateService.instant('users.grid.re-inviteMsg'),
        cancelText: this.translateService.instant('form.button.cancel'),
        confirmText: this.translateService.instant('form.button.continue'),
      };

      this.dialogRef = this.dialog.open(ConfirmationComponent, {
        data: modalContent,
      });

      this.dialogRef.afterClosed().subscribe((confirmAction: boolean) => {
        if (confirmAction) {
          this.usersService.invite(userId, this.isGlobalTenant).subscribe({
            next: () => {
              this.reInviteSuccess = true;
              this.reInvitedUser = '';
            },
            error: (err) => {
              if (err?.error?.errorCode === '020401') {
                this.statusError = true;
                if (this.form?.get('status') && err.error.message.includes('active')) {
                  this.form?.get('status').setValue('active');
                } else if (this.form?.get('status') && err.error.message.includes('disabled')) {
                  this.form?.get('status').setValue('disabled');
                }
              } else {
                this.reInviteError = true;
              }
              this.reInvitedUser = '';
              throwError(err);
            },
          });
        } else {
          this.reInvitedUser = '';
        }
      });
    }

    handleMultiTenantUser(saveData) {
      if (saveData.roles) {
        saveData.roles = Object.keys(saveData.roles).reduce((obj, key) => {
          if (!saveData.tenantIds.includes(key)) {
            return obj;
          }

          obj[key] = saveData.roles[key].map((role) => role.id);
          return obj;
        }, {});
      }

      if (saveData.storeIds) {
        saveData.storeIds = Object.keys(saveData.storeIds).reduce((obj, key) => {
          if (!saveData.tenantIds.includes(key)) {
            return obj;
          }

          obj[key] = saveData.storeIds[key];
          return obj;
        }, {});
      }

      if (Array.isArray(saveData.tenantIds)) {
        // backend needed to have the same order in all places
        saveData.tenantIds?.sort((a, b) => (a < b ? 1 : -1));
      }
    }

    onStatusChange(event, user) {
      this.isStatusChange = true;
      this.changedUserId = user.id;
      this.resetMessages();
      let modalContent = {
        title: this.translateService.instant('users.grid.statusChange'),
        message: this.translateService.instant('users.grid.statusChangeMsg', {
          change: user.status === 'active' ? 'disable' : 'enable',
          name: user.name,
        }),
        cancelText: this.translateService.instant('form.button.cancel'),
        confirmText: this.translateService.instant('form.button.continue'),
      };

      this.dialogRef = this.dialog.open(ConfirmationComponent, {
        data: modalContent,
      });

      this.dialogRef.afterClosed().subscribe((confirmAction: boolean) => {
        if (confirmAction) {
          user.status = user.status === 'active' ? 'disabled' : 'active';

          const newUser = DeepCloneService.clone(user);

          this.handleMultiTenantUser(newUser);
          this.usersService
            .update(newUser)
            .pipe(first())
            .subscribe({
              next: () => {
                this.isStatusChange = false;
                this.statusChangeSuccess = true;
                this.form?.markAsPristine();
              },
              error: (err) => {
                this.isStatusChange = false;
                user.status = user.status === 'active' ? 'disabled' : 'active';
                this.statusChangeError = true;
                this.form?.markAsPristine();
              },
            });
        } else {
          this.isStatusChange = false;
          event.source.checked = user.status === 'active';
          if (this.form?.get('status')) {
            this.form?.get('status').setValue(user.status === 'active');
          }
          this.form?.markAsPristine();
        }
      });
    }

    onDelete(user) {
      this.isDeleteUser = true;
      this.changedUserId = user.id;
      this.resetMessages();
      let modalContent = {
        title: this.translateService.instant('users.grid.deleteUser'),
        message: this.translateService.instant('users.grid.deleteUserMsg', {
          name: user.name,
        }),
        cancelText: this.translateService.instant('form.button.cancel'),
        confirmText: this.translateService.instant('form.button.continue'),
      };

      this.dialogRef = this.dialog.open(ConfirmationComponent, {
        data: modalContent,
      });

      this.dialogRef.afterClosed().subscribe((confirmAction: boolean) => {
        if (confirmAction) {
          this.usersService
            .delete(user.id, this.isGlobalTenant)
            .pipe(first())
            .subscribe({
              next: () => {
                this.isDeleteUser = false;
                this.deleteUserSuccess = true;
                this.changedUserId = '';
                if (this.users) {
                  this.users = this.users?.filter((item) => item.id !== user.id);
                  this.dataSource.data = [...this.users];
                } else {
                  this.onBack();
                  this.users = this.users?.filter((item) => item.id !== user.id);
                  this.form?.markAsPristine();
                }
              },
              error: (err) => {
                this.isDeleteUser = false;
                this.deleteUserError = true;
                this.changedUserId = '';
                this.form?.markAsPristine();
              },
            });
        } else {
          this.isDeleteUser = false;
          this.changedUserId = '';
          this.form?.markAsPristine();
        }
      });
    }

    onResetPassword(user) {
      this.isResetPassword = true;
      this.changedUserId = user.id;
      this.resetMessages();
      let modalContent = {
        title: this.translateService.instant('users.grid.resetPassword'),
        message: this.translateService.instant('users.grid.resetPasswordMsg', {
          name: user.name,
        }),
        cancelText: this.translateService.instant('form.button.cancel'),
        confirmText: this.translateService.instant('form.button.continue'),
      };

      this.dialogRef = this.dialog.open(ConfirmationComponent, {
        data: modalContent,
      });

      this.dialogRef.afterClosed().subscribe((confirmAction: boolean) => {
        if (confirmAction) {
          this.usersService
            .resetPassword(user.id, this.isGlobalTenant)
            .pipe(first())
            .subscribe({
              next: () => {
                this.isResetPassword = false;
                this.resetPasswordSuccess = true;
                this.changedUserId = '';
                this.form?.markAsPristine();
              },
              error: (err) => {
                this.isResetPassword = false;
                this.resetPasswordError = true;
                this.changedUserId = '';
                this.form?.markAsPristine();
              },
            });
        } else {
          this.isResetPassword = false;
          this.changedUserId = '';
          this.form?.markAsPristine();
        }
      });
    }

    resetMessages() {
      this.statusChangeError = false;
      this.statusChangeSuccess = false;
      this.deleteUserError = false;
      this.deleteUserSuccess = false;
      this.reInviteSuccess = false;
      this.reInviteError = false;
      this.statusError = false;
      this.onboardBrandSuccess = false;
      this.onboardBrandError = false;
      this.emptyUsersMsg = false;
      this.showLoadError = false;
    }
  };
